import { marketplaceTitle } from "@/api/returns";
import type {
  BypassCounts,
  DwhExport,
  DwhOpsDiff,
  Excluded,
  MartStatus,
  OpFact,
  OpReason,
  OpsDirection,
  OpRow,
  OpsReport,
  OpTotal,
  PostingSync,
  RateKind,
} from "@/api/warehouse_ops";
import { TARIFFS_EDIT_PERMISSION } from "@/api/warehouse_ops";
import { groupSscc } from "@/lib/boxes";
import { toISODate } from "@/lib/date-range";
import { todayIso } from "@/lib/okk-receiving";
import { fmtDate, ruNum, ruRub } from "@/lib/format";

/** Логика экрана «Операции и стоимость» — без React, под тесты.
 *
 * Главное правило экрана: незаданный тариф ВИДЕН словами («тариф не задан»),
 * а не нулём — ноль читался бы как посчитанная бесплатная операция. */

export const TARIFF_UNSET_TEXT = "тариф не задан";
export const TARIFF_NA_TEXT = "нет тарифа";
/** Справочная строка (вне категории «Производство маркетплейсы ГП»): не считается вовсе. */
export const REFERENCE_TEXT = "не считается";

export function fmtRub(n: number): string {
  return `${ruRub.format(n)} ₽`;
}

/** Тариф — до 4 знаков (колонка Numeric(14,4)): 52,125 ₽ не должно показываться 52,13. */
const ruTariff = new Intl.NumberFormat("ru-RU", {
  maximumFractionDigits: 4,
});

export function fmtTariff(n: number): string {
  return `${ruTariff.format(n)} ₽`;
}

const MSK_DATE_TIME = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Момент → `01.10.2026, 10:00` по Москве: день операции считает сервер по МСК,
 *  и время в провале обязано сходиться с ним, а не с поясом браузера. */
export function fmtDateTimeMsk(iso: string | null | undefined): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return MSK_DATE_TIME.format(at);
}

/** Ячейка «тариф» строки дня. */
export function tariffCell(row: OpRow): string {
  if (row.reference) return REFERENCE_TEXT;
  if (row.tariff_state === "set" && row.tariff_value !== null) {
    const since = row.tariff_valid_from ? ` с ${fmtDate(row.tariff_valid_from)}` : "";
    return `${fmtTariff(row.tariff_value)}${since}`;
  }
  return row.tariff_state === "unset" ? TARIFF_UNSET_TEXT : TARIFF_NA_TEXT;
}

/** Ячейка «сумма» строки дня: без тарифа суммы нет — и это сказано словами. */
export function amountCell(row: OpRow): string {
  if (row.reference) return REFERENCE_TEXT;
  if (row.amount !== null) return fmtRub(row.amount);
  return row.tariff_state === "unset" ? TARIFF_UNSET_TEXT : "—";
}

/** Итог направления × операции: сумма по строкам с тарифом и, рядом, объём,
 *  оставшийся без суммы — иначе итог выглядел бы полным при недоборе. */
export function totalAmountCell(
  total: Pick<OpTotal, "volume" | "amount" | "volume_unpriced" | "unit"> & { reference?: boolean },
): string {
  if (total.reference) return REFERENCE_TEXT;
  if (total.volume_unpriced === 0) return fmtRub(total.amount);
  const tail = `${ruNum.format(total.volume_unpriced)} ${total.unit} без суммы`;
  if (total.volume_unpriced === total.volume) return tail;
  return `${fmtRub(total.amount)} + ${tail}`;
}

/** Объём без суммы в одной единице. */
export interface UnitVolume {
  unit: string;
  volume: number;
}

/** Сумма по направлению — тем же правилом, что итог операции.
 *
 *  Объём без суммы — ПО ЕДИНИЦАМ: паллето-дни, коробы и штуки не складываются
 *  в одно «ед.» (такое число ничего не значит). `nothingPriced` — у направления
 *  нет ни одной суммы: карточка говорит «тариф не задан», а не «0 ₽». */
export interface DirectionSum {
  direction: string;
  title: string;
  amount: number;
  unpriced: UnitVolume[];
  nothingPriced: boolean;
  /** Весь объём без суммы — «без тарифа по замыслу» (см. `Priced.unpricedNa`). */
  unpricedNa: boolean;
  /** У направления только справочные строки (вне категории ГП) — «не считается». */
  onlyReference: boolean;
}

/** Итог (или его часть), который умеют складывать карточки, матрица и сводная. */
export type Priced = Pick<
  OpTotal,
  "direction" | "direction_title" | "unit" | "volume" | "amount" | "volume_unpriced"
> & {
  /** ВЕСЬ объём без суммы — у пар, где тарифа нет по замыслу (`not_applicable`),
   *  а не потому что его забыли задать. Нет флага — «тариф не задан». */
  unpricedNa?: boolean;
  /** Справочная строка (вне категории ГП): объём виден, в суммы и «без суммы» не идёт. */
  reference?: boolean;
};

/** Итог пары с признаком «без тарифа по замыслу». */
export type PricedTotal = OpTotal & { unpricedNa: boolean };

/** Пары (направление, операция), ВСЕ строки которых — `not_applicable`. Итог OpTotal
 *  состояния тарифа не несёт, поэтому оно берётся из строк отчёта. Направление
 *  «Не определено» сюда не входит: там тарифа нет из-за недоопределённости, а не по замыслу.
 *  Смешанная пара (часть строк `unset`) — не входит: «тариф не задан» остаётся. */
export function notApplicablePairs(rows: Pick<OpRow, "direction" | "operation" | "tariff_state">[]): Set<string> {
  const all = new Map<string, boolean>();
  for (const r of rows) {
    const k = `${r.direction}|${r.operation}`;
    all.set(k, (all.get(k) ?? true) && r.tariff_state === "not_applicable" && r.direction !== "undefined");
  }
  return new Set([...all].filter(([, v]) => v).map(([k]) => k));
}

export function directionSums(totals: Priced[]): DirectionSum[] {
  const out: DirectionSum[] = [];
  for (const t of totals) {
    let d = out.find((x) => x.direction === t.direction);
    if (!d) {
      d = {
        direction: t.direction,
        title: t.direction_title,
        amount: 0,
        unpriced: [],
        nothingPriced: true,
        unpricedNa: true,
        onlyReference: true,
      };
      out.push(d);
    }
    // Вне категории ГП — справочно: ни в сумму, ни в «без суммы» (это не недобор).
    if (t.reference) continue;
    d.onlyReference = false;
    d.amount += t.amount;
    if (t.volume === 0 || t.volume_unpriced < t.volume) d.nothingPriced = false;
    if (t.volume_unpriced > 0) {
      if (!t.unpricedNa) d.unpricedNa = false;
      const u = d.unpriced.find((x) => x.unit === t.unit);
      if (u) u.volume += t.volume_unpriced;
      else d.unpriced.push({ unit: t.unit, volume: t.volume_unpriced });
    }
  }
  return out;
}

/** «2 паллета, 15 коробов» — объём без суммы по единицам, через запятую. */
export function unpricedText(unpriced: UnitVolume[]): string {
  return unpriced.map((u) => `${ruNum.format(u.volume)} ${u.unit}`).join(", ");
}

// --- ячейки итогов: сумма крупно, объём мелко ------------------------------------

/** «Не определено» — направления нет, значит, нет и тарифа: показывается объём. */
export const NO_DIRECTION_TARIFF_TEXT = "без тарифа";

/** Итоги строки/столбца/всего, где объём без суммы — только «без тарифа по замыслу». */
export const NO_AMOUNT_TEXT = "без суммы";

/** Что показать в ячейке итога: `main` — крупно, `sub` — мелко под ним,
 *  `warn` — янтарём (суммы нет или она не полная), `hint` — подсказка. */
export interface CellView {
  main: string;
  sub: string;
  warn: boolean;
  hint: string;
}

/** Итог направления × операции (или строки «Итого»): без суммы — словами,
 *  неполная сумма — с объёмом без суммы рядом. Ноль не появляется никогда. */
export function totalView(t: Priced): CellView {
  const vol = `${ruNum.format(t.volume)} ${t.unit}`;
  if (t.reference) {
    return { main: REFERENCE_TEXT, sub: vol, warn: false, hint: `${vol} — вне категории ГП, ${REFERENCE_TEXT}` };
  }
  if (t.direction === "undefined") {
    return { main: vol, sub: NO_DIRECTION_TARIFF_TEXT, warn: true, hint: `${vol} — ${NO_DIRECTION_TARIFF_TEXT}` };
  }
  if (t.volume_unpriced === 0) {
    return { main: fmtRub(t.amount), sub: vol, warn: false, hint: `${fmtRub(t.amount)} за ${vol}` };
  }
  if (t.volume_unpriced === t.volume) {
    // Тарифа нет по замыслу — нейтрально, теми же словами, что ячейки дней.
    if (t.unpricedNa) return { main: TARIFF_NA_TEXT, sub: vol, warn: false, hint: `${vol} — ${TARIFF_NA_TEXT}` };
    return { main: TARIFF_UNSET_TEXT, sub: vol, warn: true, hint: `${vol} — ${TARIFF_UNSET_TEXT}` };
  }
  return {
    main: fmtRub(t.amount),
    sub: `${vol}, из них ${ruNum.format(t.volume_unpriced)} без суммы`,
    warn: !t.unpricedNa,
    hint: totalAmountCell(t),
  };
}

/** Сумма направления (или общий итог): объёмы разных единиц не складываются —
 *  без суммы остаётся «2 паллета, 15 коробов». */
export function sumView(s: DirectionSum): CellView {
  const unpriced = unpricedText(s.unpriced);
  if (s.onlyReference) {
    return { main: REFERENCE_TEXT, sub: "", warn: false, hint: `только вне категории ГП — ${REFERENCE_TEXT}` };
  }
  if (s.direction === "undefined") {
    return { main: NO_DIRECTION_TARIFF_TEXT, sub: unpriced, warn: true, hint: `${unpriced} — ${NO_DIRECTION_TARIFF_TEXT}` };
  }
  if (s.nothingPriced) {
    const sub = unpriced ? `${unpriced} без суммы` : "";
    if (s.unpricedNa && unpriced) return { main: NO_AMOUNT_TEXT, sub: unpriced, warn: false, hint: sub };
    return { main: TARIFF_UNSET_TEXT, sub, warn: true, hint: sub || TARIFF_UNSET_TEXT };
  }
  const sub = unpriced ? `+ ${unpriced} без суммы` : "";
  return {
    main: fmtRub(s.amount),
    sub,
    warn: unpriced !== "" && !s.unpricedNa,
    hint: sub ? `${fmtRub(s.amount)} ${sub}` : fmtRub(s.amount),
  };
}

/** Складывает части в один итог той же единицы (строка сводной, строка матрицы). */
export function sumPriced(
  parts: Priced[],
  head: Pick<Priced, "direction" | "direction_title" | "unit" | "reference">,
): Priced {
  const out: Priced = { ...head, volume: 0, amount: 0, volume_unpriced: 0, unpricedNa: true };
  for (const p of parts) {
    if (p.volume_unpriced > 0 && !p.unpricedNa) out.unpricedNa = false;
    out.volume += p.volume;
    out.amount += p.amount;
    out.volume_unpriced += p.volume_unpriced;
  }
  return out;
}

/** Строка дня как часть итога — тем же правилом, что сервер (`build_totals`):
 *  сумма только у строки с тарифом, объём без тарифа идёт в `volume_unpriced`. */
export function rowAsPriced(r: OpRow): Priced {
  return {
    direction: r.direction,
    direction_title: r.direction_title,
    unit: r.unit,
    volume: r.volume,
    amount: r.amount ?? 0,
    volume_unpriced: r.amount === null ? r.volume : 0,
    unpricedNa: r.tariff_state === "not_applicable" && r.direction !== "undefined",
    reference: r.reference,
  };
}

/** Общий итог по суммам направлений: единицы без суммы — по-прежнему раздельно. */
function mergeSums(sums: DirectionSum[]): DirectionSum {
  const out: DirectionSum = {
    direction: sums.length > 0 && sums.every((s) => s.direction === "undefined") ? "undefined" : "all",
    title: "Итого",
    amount: 0,
    unpriced: [],
    nothingPriced: true,
    unpricedNa: true,
    onlyReference: sums.length > 0 && sums.every((s) => s.onlyReference),
  };
  for (const s of sums) {
    out.amount += s.amount;
    if (s.unpriced.length > 0 && !s.unpricedNa) out.unpricedNa = false;
    if (!s.nothingPriced) out.nothingPriced = false;
    for (const u of s.unpriced) {
      const x = out.unpriced.find((v) => v.unit === u.unit);
      if (x) x.volume += u.volume;
      else out.unpriced.push({ ...u });
    }
  }
  return out;
}

function mergeReasons(lists: OpReason[][]): OpReason[] {
  const out: OpReason[] = [];
  for (const list of lists) {
    for (const r of list) {
      const x = out.find((v) => v.reason === r.reason);
      if (x) x.volume += r.volume;
      else out.push({ ...r });
    }
  }
  return out;
}

/** «Заказ собирали не в портале: 1» — причины построчно, для подсказки. */
export function reasonsText(reasons: OpReason[]): string {
  return reasons.map((r) => `${r.title}: ${ruNum.format(r.volume)}`).join("\n");
}

/** Слияние упорядоченных цепочек в один порядок (Кан): каждая цепочка — порядок
 *  сервера внутри одного направления; ничья — по первому появлению. */
function mergeChains(chains: string[][]): { order: string[]; problem: string | null } {
  const seen: string[] = [];
  const after = new Map<string, Set<string>>();
  const indeg = new Map<string, number>();
  for (const chain of chains) {
    chain.forEach((k, i) => {
      if (!indeg.has(k)) {
        indeg.set(k, 0);
        after.set(k, new Set());
        seen.push(k);
      }
      const prev = chain[i - 1];
      if (prev !== undefined && !after.get(prev)!.has(k)) {
        after.get(prev)!.add(k);
        indeg.set(k, indeg.get(k)! + 1);
      }
    });
  }
  const out: string[] = [];
  while (out.length < seen.length) {
    const next = seen.find((k) => indeg.get(k) === 0 && !out.includes(k));
    // Цикл — только если сервер сломал сортировку итогов: молча не чиним и не
    // роняем рендер (в портале нет ErrorBoundary) — отдаём как нарушение ответа.
    if (next === undefined) {
      return {
        order: out,
        problem: `Порядок операций в ответе сервера противоречив: ${seen.filter((k) => !out.includes(k)).join(", ")}`,
      };
    }
    out.push(next);
    for (const k of after.get(next)!) indeg.set(k, indeg.get(k)! - 1);
  }
  return { order: out, problem: null };
}

// --- «Итого за период»: статья × направление -------------------------------------

export interface MatrixRow {
  operation: string;
  title: string;
  unit: string;
  /** По столбцам `columns`; `null` — у направления такой статьи нет. */
  cells: (PricedTotal | null)[];
  total: Priced;
  reasons: OpReason[];
}

export interface TotalsMatrix {
  columns: { direction: OpsDirection; title: string }[];
  rows: MatrixRow[];
  /** Итог направления — по столбцам `columns`. */
  columnTotals: DirectionSum[];
  grand: DirectionSum;
  /** Нарушения ответа сервера — показываются на экране; при них строк нет. */
  problems: string[];
}

/** Итоги периода матрицей: строки — статьи (одна операция во всех направлениях),
 *  столбцы — направления в порядке сервера, «Итого» строки и столбца. */
export function buildTotalsMatrix(allTotals: OpTotal[], rows: OpRow[]): TotalsMatrix {
  const na = notApplicablePairs(rows);
  const totals: PricedTotal[] = allTotals.map((t) => ({
    ...t,
    unpricedNa: na.has(`${t.direction}|${t.operation}`),
  }));
  const columns: TotalsMatrix["columns"] = [];
  const chains = new Map<string, string[]>();
  for (const t of totals) {
    if (!columns.some((c) => c.direction === t.direction)) {
      columns.push({ direction: t.direction, title: t.direction_title });
      chains.set(t.direction, []);
    }
    chains.get(t.direction)!.push(t.operation);
  }
  const merged = mergeChains([...chains.values()]);
  const sums = directionSums(totals);
  const columnTotals = columns.map((c) => sums.find((s) => s.direction === c.direction)!);
  if (merged.problem !== null) {
    return { columns, rows: [], columnTotals, grand: mergeSums(columnTotals), problems: [merged.problem] };
  }
  const matrixRows: MatrixRow[] = merged.order.map((op) => {
    const cells = columns.map(
      (c) => totals.find((t) => t.direction === c.direction && t.operation === op) ?? null,
    );
    const present = cells.filter((c): c is PricedTotal => c !== null);
    const first = present[0]!;
    // Столбец «Не определено» — последний: первая ячейка строки — «undefined»,
    // только если других направлений у статьи нет (тогда и итог «без тарифа»).
    return {
      operation: op,
      title: first.operation_title,
      unit: first.unit,
      cells,
      total: sumPriced(present, {
        direction: first.direction,
        direction_title: "Итого",
        unit: first.unit,
        reference: first.reference,
      }),
      reasons: mergeReasons(present.map((c) => c.reasons)),
    };
  });
  return { columns, rows: matrixRows, columnTotals, grand: mergeSums(columnTotals), problems: [] };
}

// --- «По дням»: сводная направление/операция × день -------------------------------

const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

export interface PivotDay {
  iso: string;
  /** `01.10` */
  label: string;
  /** `чт` */
  weekday: string;
  weekend: boolean;
}

export interface PivotLine {
  direction: OpsDirection;
  direction_title: string;
  operation: string;
  operation_title: string;
  unit: string;
  /** Справочная строка (вне категории ГП) — «не считается». */
  reference: boolean;
  /** По дням `days`; `null` — в этот день операции не было. */
  cells: (OpRow | null)[];
  total: Priced;
}

export interface PivotGroup {
  direction: OpsDirection;
  title: string;
  lines: PivotLine[];
  /** Сумма направления за день; `null` — в этот день у направления ничего. */
  daySums: (DirectionSum | null)[];
  total: DirectionSum;
}

export interface DayPivot {
  days: PivotDay[];
  groups: PivotGroup[];
  /** Нарушения ответа сервера — показываются на экране, строки не теряются. */
  problems: string[];
}

/** Все дни периода — включая дни без операций: календарь не сжимается. */
export function periodDays(dateFrom: string, dateTo: string): PivotDay[] {
  const out: PivotDay[] = [];
  const [y, m, d] = dateFrom.split("-").map(Number);
  const cur = new Date(y!, m! - 1, d!);
  for (let iso = toISODate(cur); iso <= dateTo; iso = toISODate(cur)) {
    const wd = cur.getDay();
    out.push({
      iso,
      label: `${iso.slice(8, 10)}.${iso.slice(5, 7)}`,
      weekday: WEEKDAYS[wd]!,
      weekend: wd === 0 || wd === 6,
    });
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Сводная «По дням»: столбцы — каждый день периода, группы — направления,
 *  строки — операции. Порядок пар (направление, операция) — порядок итогов
 *  сервера (`build_totals` сортирует их так же, как строки дня). */
export function buildDayPivot(
  rows: OpRow[],
  totals: OpTotal[],
  dateFrom: string,
  dateTo: string,
): DayPivot {
  const days = periodDays(dateFrom, dateTo);
  const dayIdx = new Map(days.map((d, i) => [d.iso, i]));
  const problems: string[] = [];
  const key = (r: Pick<OpRow, "direction" | "operation">) => `${r.direction}|${r.operation}`;
  const lines = new Map<string, PivotLine>();
  const newLine = (r: OpRow | OpTotal): PivotLine => ({
    direction: r.direction,
    direction_title: r.direction_title,
    operation: r.operation,
    operation_title: r.operation_title,
    unit: r.unit,
    reference: r.reference,
    cells: days.map(() => null),
    total: {
      direction: r.direction,
      direction_title: r.direction_title,
      unit: r.unit,
      volume: 0,
      amount: 0,
      volume_unpriced: 0,
      reference: r.reference,
    },
  });
  for (const t of totals) {
    if (rows.some((r) => key(r) === key(t))) lines.set(key(t), newLine(t));
    else problems.push(`нет строк по дням для итога «${t.direction_title} — ${t.operation_title}»`);
  }
  for (const r of rows) {
    const i = dayIdx.get(r.day);
    if (i === undefined) {
      problems.push(`${fmtDate(r.day)} вне периода: ${r.direction_title} — ${r.operation_title}`);
      continue;
    }
    let line = lines.get(key(r));
    if (!line) {
      problems.push(`нет итога для «${r.direction_title} — ${r.operation_title}»`);
      line = newLine(r);
      lines.set(key(r), line);
    }
    if (line.cells[i]) {
      problems.push(`${fmtDate(r.day)}: «${r.direction_title} — ${r.operation_title}» пришла дважды`);
      continue;
    }
    line.cells[i] = r;
  }
  const groups: PivotGroup[] = [];
  for (const line of lines.values()) {
    const present = line.cells.filter((c): c is OpRow => c !== null).map(rowAsPriced);
    line.total = sumPriced(present, line);
    let g = groups.find((x) => x.direction === line.direction);
    if (!g) {
      g = { direction: line.direction, title: line.direction_title, lines: [], daySums: [], total: emptySum(line) };
      groups.push(g);
    }
    g.lines.push(line);
  }
  for (const g of groups) {
    g.daySums = days.map((_, i) => {
      const parts = g.lines.flatMap((l) => (l.cells[i] ? [rowAsPriced(l.cells[i]!)] : []));
      return parts.length > 0 ? directionSums(parts)[0]! : null;
    });
    g.total = directionSums(g.lines.map((l) => l.total))[0]!;
  }
  return { days, groups, problems };
}

function emptySum(l: Pick<PivotLine, "direction" | "direction_title">): DirectionSum {
  return {
    direction: l.direction,
    title: l.direction_title,
    amount: 0,
    unpriced: [],
    nothingPriced: true,
    unpricedNa: true,
    onlyReference: true,
  };
}

/** Что-то в ячейке дня требует внимания: причины «не определено», недостача
 *  на паллетах, норма не задана. Тариф не задан виден в режиме «Сумма» словами. */
export function cellFlagged(row: OpRow): boolean {
  if (row.reasons.length > 0) return true;
  const b = row.breakdown;
  if (!b) return false;
  if (b.shortfall_qty) return true;
  return row.operation === "storage_pallets" && (b.norm === undefined || b.boxes === undefined);
}

/** Ячейка дня в режиме «Сумма»: без тарифа — словами, не нулём. */
export function pivotAmountText(row: OpRow): string {
  if (row.reference) return REFERENCE_TEXT;
  if (row.amount !== null) return fmtRub(row.amount);
  return row.tariff_state === "unset" ? TARIFF_UNSET_TEXT : TARIFF_NA_TEXT;
}

/** Подсказка ячейки дня: объём, тариф, сумма, из чего сложено, причины. */
export function cellHint(row: OpRow): string {
  const lines = [
    `${fmtDate(row.day)} · ${row.direction_title} · ${row.operation_title}`,
    `Объём: ${ruNum.format(row.volume)} ${row.unit}`,
    `Тариф: ${tariffCell(row)}`,
    `Сумма: ${amountCell(row)}`,
  ];
  const b = breakdownText(row);
  if (b) lines.push(b);
  if (row.reasons.length > 0) lines.push(reasonsText(row.reasons));
  return lines.join("\n");
}

/** Операции, при которых итог хранения/приёмки не досчитан (без вложенности / без нормы). */
const PARTIAL_ACCEPTANCE_OPS = ["acceptance_no_box", "acceptance_boxes"];

/** Почему итог периода неполный — словами; пусто — полный.
 *
 *  Не только «витрины недоступны»: дни без снимка остатка (сегодня — до сборки
 *  DWH ~10:30), приёмка без вложенности или нормы, позиции хранения без
 *  вложенности и паллеты, на которых больше, чем в остатке, — всё это недобор
 *  итога, и показывается рядом с ним. */
export function partialReasons(report: OpsReport, today: string): string[] {
  const out: string[] = [];
  const m = report.mart;
  if (!m.ok) {
    out.push("без хранения и приёмки (витрины DWH недоступны — причина выше)");
    return out;
  }
  const missing = m.missing_days;
  const past = missing.filter((d) => d < today);
  if (past.length > 0) {
    out.push(
      `нет снимка остатка за ${past.map(fmtDate).join(", ")} — хранение за эти дни не посчитано`,
    );
  }
  if (missing.includes(today)) {
    out.push("за сегодня данных ещё нет, сборка DWH ~10:30 — хранение за сегодня не посчитано");
  }
  const future = missing.filter((d) => d > today);
  if (future.length > 0) {
    out.push(`дни после сегодня (${future.length} дн.) ещё не наступили — хранения за них нет`);
  }
  const byOp = (ops: string[]) => report.totals.filter((t) => ops.includes(t.operation));
  const acceptance = byOp(PARTIAL_ACCEPTANCE_OPS);
  if (acceptance.length > 0) {
    const parts = acceptance.map((t) => `${t.operation_title.toLowerCase()} — ${ruNum.format(t.volume)} ${t.unit}`);
    out.push(`приёмка неполная: ${parts.join("; ")}`);
  }
  const noBox = byOp(["storage_no_box"]);
  if (noBox.length > 0) {
    const vol = noBox.reduce((a, t) => a + t.volume, 0);
    out.push(
      `хранение без вложенности короба не досчитано: ${ruNum.format(vol)} ${noBox[0]!.unit}`,
    );
  }
  const sync = report.posting_sync;
  if (sync && !sync.ok) {
    out.push(
      "сверка проведения расходных с 1С не работает — собранные паллеты, отгруженные мимо " +
        "портала, могут считаться «без сигнала» (по норме), а не фактом",
    );
  }
  if (report.excluded && !report.excluded.known) {
    out.push(report.excluded.title);
  }
  const st = report.standing;
  if (st && st.shortfall_days > 0) {
    out.push(
      `на собранных паллетах больше, чем в остатке: ${ruNum.format(st.shortfall_qty)} шт. ` +
        `(${ruNum.format(st.shortfall_days)} дн.-скл.) — вычтено не всё`,
    );
  }
  return out;
}

/** Правка тарифов: право (или админ) И раздел включён в профиле портала —
 *  тот же гейт, что у меню и роута (`hasPage`). */
export function canEditTariffs(
  user: { is_admin: boolean; permissions: string[] } | null | undefined,
  hasPage: (page: string) => boolean,
): boolean {
  if (!user || !hasPage(TARIFFS_EDIT_PERMISSION)) return false;
  return user.is_admin || user.permissions.includes(TARIFFS_EDIT_PERMISSION);
}

/** Открывается ли страница «Тарифы склада» (`/settings/warehouse-billing`): раздел и
 *  секция настроек включены в профиле, у пользователя право просмотра (админ проходит).
 *  Та же проверка, что у роута, — по ней же показывается ссылка на странице отчёта. */
export function canOpenTariffs(
  user: { is_admin: boolean; permissions: string[] } | null | undefined,
  hasPage: (page: string) => boolean,
  hasSection: (section: string) => boolean,
): boolean {
  if (!user || !hasPage("warehouse_billing") || !hasSection("warehouse_billing")) return false;
  return user.is_admin || user.permissions.includes("warehouse_billing");
}

/** Сегодня по Москве — для периода по умолчанию. */
export function todayMsk(): string {
  return todayIso();
}

/** Период при входе: с 1-го числа текущего месяца по сегодня. */
export function defaultOpsPeriod(todayIso: string): [string, string] {
  return [`${todayIso.slice(0, 8)}01`, todayIso];
}

/** Проверка значения версии до отправки: текст ошибки или пусто.
 *  Сервер проверяет то же самое — здесь только чтобы не гонять заведомо
 *  неверное (запятая как разделитель — норма для русского ввода). */
export function rateValueError(raw: string, kind: RateKind["kind"]): string {
  const s = raw.trim().replace(",", ".");
  if (!s) return "Введите значение";
  if (!/^\d+(\.\d+)?$/.test(s)) return "Число, например 52,10";
  if (/\.\d{5,}$/.test(s)) return "Не больше 4 знаков после запятой";
  if (s.split(".")[0]!.length > 10) return "Слишком большое значение";
  const n = Number(s);
  if (kind === "norm" && (!Number.isInteger(n) || n <= 0)) {
    return "Норма — целое число больше нуля";
  }
  return "";
}

export function normalizeRateValue(raw: string): string {
  return raw.trim().replace(",", ".");
}

export function fmtRateValue(kind: RateKind, value: number): string {
  return kind.kind === "norm" ? `${ruNum.format(value)} ${kind.unit}` : fmtTariff(value);
}

/** Операции из витрин DWH (хранение и приёмка) — провал у них свой. */
export const STORAGE_OPS = [
  "storage_pallets",
  "storage_boxes",
  "storage_no_box",
  "storage_out_of_scope",
];
export const ACCEPTANCE_OPS = [
  "acceptance",
  "acceptance_boxes",
  "acceptance_no_box",
  "acceptance_out_of_scope",
];

/** Из чего сложен объём хранения/приёмки — строкой под операцией. Пусто — нечего сказать. */
export function breakdownText(row: Pick<OpRow, "operation" | "breakdown">): string {
  const b = row.breakdown;
  if (!b) return "";
  const parts: string[] = [];
  if (row.operation === "storage_pallets") {
    if (b.norm !== undefined && b.boxes !== undefined) {
      parts.push(
        `${ruNum.format(b.boxes)} кор. / ${ruNum.format(b.norm)} = ${ruNum.format(b.boxes / b.norm)}`,
      );
    } else {
      parts.push("норма не задана — только собранные паллеты");
    }
    if (b.standing) parts.push(`+ ${ruNum.format(b.standing)} собранн. паллет фактом`);
    if (b.shortfall_qty) {
      parts.push(`· на паллетах больше, чем в остатке: ${ruNum.format(b.shortfall_qty)} шт.`);
    }
  } else if (row.operation === "storage_no_box") {
    parts.push(`${ruNum.format(b.positions ?? 0)} поз. без вложенности — не досчитано`);
  } else if (row.operation === "storage_out_of_scope") {
    parts.push(`${ruNum.format(b.positions ?? 0)} поз. вне «Производство маркетплейсы ГП» — справочно`);
  } else if (row.operation === "acceptance_out_of_scope") {
    parts.push(
      `${ruNum.format(b.lines ?? 0)} стр., ${ruNum.format(b.docs ?? 0)} док. вне «Производство маркетплейсы ГП» — справочно`,
    );
  } else if (row.operation === "acceptance") {
    if (b.norm !== undefined && b.boxes !== undefined) {
      parts.push(`${ruNum.format(b.boxes)} кор. / ${ruNum.format(b.norm)}`);
    }
    parts.push(`${ruNum.format(b.lines ?? 0)} стр., ${ruNum.format(b.docs ?? 0)} док.`);
  } else if (row.operation === "acceptance_boxes" || row.operation === "acceptance_no_box") {
    parts.push(`${ruNum.format(b.lines ?? 0)} стр., ${ruNum.format(b.docs ?? 0)} док.`);
  }
  return parts.join(" ");
}

/** Состояние витрин одной строкой: по какую дату остаток и когда собраны. */
export function martStatusText(m: MartStatus): string {
  if (!m.ok) return "";
  const dates = m.warehouses
    .filter((w) => w.in_stock_mart && w.stock_date_to)
    .map((w) => `${w.warehouse_name} — по ${fmtDate(w.stock_date_to!)}`);
  const built = m.built_at ? `, собраны ${fmtDateTimeMsk(m.built_at)}` : "";
  return `Витрины склада DWH: остаток ${dates.join("; ")}${built}`;
}

/** Строка сверки «передано в DWH / сейчас по порталу» (`dwh_ops.py`).
 *  `tone`: `neutral` — передачи ещё не было (это не ошибка и не 0), `ok` — совпадает,
 *  `diff` — расходится (статьи — в `diffs` ответа), `error` — сверить не удалось. */
export interface DwhLineView {
  tone: "neutral" | "ok" | "diff" | "error";
  text: string;
  notes: string[];
}

function fmtSpan(from: string, to: string): string {
  return from === to ? fmtDate(from) : `${fmtDate(from)}–${fmtDate(to)}`;
}

export function dwhLineView(d: DwhExport, periodFrom: string, periodTo: string): DwhLineView {
  const head = "Передано в DWH";
  const partial =
    "по порталу сейчас без хранения и приёмки (витрины склада недоступны) — их расхождение " +
    "не правка данных, а дыра на стороне портала";
  if (d.state === "not_exported") {
    return { tone: "neutral", text: `${head}: в DWH ещё не передавалось`, notes: [] };
  }
  if (d.state === "error") {
    return { tone: "error", text: `${head}: сверка не выполнена — ${d.error ?? ""}`, notes: [] };
  }
  if (d.first_exported_day === null) {
    return { tone: "error", text: `${head}: ответ сервера неполон (нет первого переданного дня)`, notes: [] };
  }
  if (d.state === "period_not_exported") {
    if (periodTo < d.first_exported_day) {
      return {
        tone: "neutral",
        text: `${head}: за этот период ещё не передавалось (передача в DWH — с ${fmtDate(d.first_exported_day)})`,
        notes: [],
      };
    }
    const last = d.exported_to ? ` (последняя передача — по ${fmtDate(d.exported_to)})` : "";
    return { tone: "neutral", text: `${head}: за этот период ещё не передавалось${last}`, notes: [] };
  }
  if (
    d.compared_from === null ||
    d.compared_to === null ||
    d.dwh_amount === null ||
    d.portal_amount === null
  ) {
    return { tone: "error", text: `${head}: ответ сервера неполон (нет дней или сумм сверки)`, notes: [] };
  }
  const min = fmtDateTimeMsk(d.computed_at_min);
  const max = fmtDateTimeMsk(d.computed_at_max);
  const at = min === max ? min : `${min} – ${max}`;
  const text =
    `${head}: за ${fmtSpan(d.compared_from, d.compared_to)} ${fmtRub(d.dwh_amount)}` +
    `${at ? ` (на ${at})` : ""}, сейчас по порталу ${fmtRub(d.portal_amount)}`;
  const notes: string[] = [];
  if (d.diffs.length > 0) {
    notes.push(`расходится по статьям: ${d.diffs.length}`);
  }
  if (d.compared_from > periodFrom) {
    notes.push(`дни до ${fmtDate(d.first_exported_day)} в DWH ещё не передавались — не сверяются`);
  }
  if (d.compared_to < periodTo) {
    notes.push(`дни после ${fmtDate(d.compared_to)} в DWH ещё не передавались — не сверяются`);
  }
  if (d.complete === false) {
    notes.push(`передано неполным: ${d.incomplete_reasons.join("; ")}`);
  }
  if (d.portal_partial) notes.push(partial);
  return { tone: d.diffs.length > 0 ? "diff" : "ok", text, notes };
}

/** Сторона статьи в расхождении: строк нет — словами, суммы нет — словами, не 0. */
export function dwhSideText(volume: number | null, amount: number | null, unit: string): string {
  if (volume === null) return "нет строк";
  const vol = `${ruNum.format(volume)}${unit ? ` ${unit}` : ""}`;
  return amount === null ? `${vol} · без суммы` : `${vol} · ${fmtRub(amount)}`;
}

export function dwhDiffTitle(d: DwhOpsDiff): string {
  return `${d.direction_title} · ${d.operation_title}`;
}

/** Колонки провала — свои у каждой группы операций и вида строки. */
export type DetailColumn = { key: string; title: string };

const KIND_COLUMNS: Record<string, DetailColumn[]> = {
  position: [
    { key: "warehouse_name", title: "Склад" },
    { key: "nomenclature_name", title: "Номенклатура" },
    { key: "article", title: "Артикул" },
    { key: "characteristic_name", title: "Характеристика" },
    { key: "category_name", title: "Категория" },
    { key: "qty", title: "Штук" },
    { key: "qty_in_box", title: "В коробе" },
    { key: "on_pallets", title: "На собранных паллетах" },
    { key: "boxes", title: "Коробов" },
    { key: "packing_issue", title: "Вложенность" },
  ],
  pallet: [
    { key: "pallet_number", title: "Паллета" },
    { key: "pallet_archive_number", title: "№ 1С (архив)" },
    { key: "order_number", title: "Заказ" },
    { key: "organization_name", title: "Юрлицо" },
    { key: "warehouse_name", title: "Склад" },
    { key: "assembled_at", title: "Собрана" },
    { key: "shipped_at", title: "Отгружена" },
    { key: "signal_title", title: "Сигнал отгрузки" },
    { key: "expense_number", title: "Документ сборки" },
    { key: "posting_state_title", title: "Документ в 1С" },
    { key: "qty", title: "Штук" },
  ],
  shortfall: [
    { key: "warehouse_name", title: "Склад" },
    { key: "nomenclature_ref", title: "Номенклатура (ссылка)" },
    { key: "characteristic_ref", title: "Характеристика (ссылка)" },
    { key: "qty", title: "Не хватило в остатке, шт." },
  ],
  day: [
    { key: "day", title: "День" },
    { key: "volume", title: "Объём" },
    { key: "boxes", title: "Коробов" },
    { key: "norm", title: "Норма" },
    { key: "standing", title: "Собранных паллет" },
    { key: "amount", title: "Сумма, ₽" },
  ],
  receipt: [
    { key: "day", title: "День" },
    { key: "doc_number", title: "Документ" },
    { key: "warehouse_name", title: "Склад" },
    { key: "nomenclature_name", title: "Номенклатура" },
    { key: "characteristic_name", title: "Характеристика" },
    { key: "category_name", title: "Категория" },
    { key: "qty", title: "Штук" },
    { key: "qty_in_box", title: "В коробе" },
    { key: "boxes", title: "Коробов" },
    { key: "packing_issue", title: "Вложенность" },
  ],
};

export const KIND_TITLES: Record<string, string> = {
  position: "Позиции остатка",
  pallet: "Собранные паллеты (фактом)",
  shortfall: "На паллетах больше, чем в остатке — вычтено не всё",
  day: "По дням",
  receipt: "Строки перемещений с упаковки",
};

export function detailColumns(operation: string, kind?: string): DetailColumn[] {
  if (kind && KIND_COLUMNS[kind]) return KIND_COLUMNS[kind]!;
  // Перемещение на фулфилмент: «кому» — склад-получатель, а не покупатель.
  const buyer = operation.endsWith("_transfer") ? "Склад-получатель" : "Покупатель";
  if (operation.startsWith("assembly")) {
    return [
      { key: "session_number", title: "Проверка №" },
      { key: "order_number", title: "Заказ" },
      { key: "organization_name", title: "Юрлицо" },
      { key: "kontragent_name", title: buyer },
      { key: "pallet_number", title: "Паллета" },
      { key: "pallet_archive_number", title: "№ 1С (архив)" },
      { key: "skus", title: "Товаров" },
      { key: "lines", title: "Сканов" },
    ];
  }
  if (operation.startsWith("shipment_bypass")) {
    return [
      { key: "order_number", title: "Заказ" },
      { key: "organization_name", title: "Юрлицо" },
      { key: "kontragent_name", title: buyer },
      { key: "pallet_number", title: "Паллета" },
      { key: "pallet_archive_number", title: "№ 1С (архив)" },
      { key: "assembled_at", title: "Собрана" },
      { key: "posted_at", title: "Дата документа в 1С" },
      { key: "expense_number", title: "Документ сборки" },
      { key: "doc_before_assembly", title: "Дата раньше сборки" },
    ];
  }
  if (operation.startsWith("shipment")) {
    return [
      { key: "order_number", title: "Заказ" },
      { key: "organization_name", title: "Юрлицо" },
      { key: "kontragent_name", title: buyer },
      { key: "pallet_number", title: "Паллета" },
    ];
  }
  return [
    { key: "marketplace", title: "Площадка" },
    { key: "cabinet_name", title: "Кабинет" },
    { key: "number", title: "Отправление" },
    { key: "supply", title: "Поставка" },
    { key: "status", title: "Статус" },
    { key: "room_name", title: "Комната" },
  ];
}

/** Строки провала, разложенные по виду (`detail.kind`): позиции, паллеты, недостача… */
export function groupFactsByKind(facts: OpFact[]): { kind: string; facts: OpFact[] }[] {
  const out: { kind: string; facts: OpFact[] }[] = [];
  for (const f of facts) {
    const kind = typeof f.detail.kind === "string" ? f.detail.kind : "";
    let g = out.find((x) => x.kind === kind);
    if (!g) {
      g = { kind, facts: [] };
      out.push(g);
    }
    g.facts.push(f);
  }
  return out;
}

/** Количества (не номера): им — разряды и до 3 знаков. */
const NUMERIC_KEYS = new Set([
  "qty",
  "on_pallets",
  "residual_qty",
  "boxes",
  "boxes_mart",
  "qty_in_box",
  "volume",
  "norm",
  "standing",
  "positions",
]);

const PACKING_ISSUES: Record<string, string> = {
  no_qty_in_box: "не задана в спецификации",
  conflict: "разная у спецификаций",
};

/** Моменты — по Москве, как считает сервер. */
const DATE_TIME_KEYS = new Set([
  "assembled_at",
  "shipped_at",
  "scanned_at",
  "posted_at",
  "posting_checked_at",
]);

export function detailValue(
  key: string,
  value: string | number | boolean | null | undefined,
): string {
  if (key === "packing_issue") return value ? (PACKING_ISSUES[String(value)] ?? String(value)) : "есть";
  if (key === "shipped_at" && !value) return "нет отметки";
  // Паллета — наш SSCC: группами по четыре, как на этикетке и в журнале.
  if (key === "pallet_number" && typeof value === "string" && /^\d{18}$/.test(value)) {
    return groupSscc(value);
  }
  if (value === null || value === undefined || value === "") return "—";
  if (key === "marketplace") return marketplaceTitle(String(value));
  if (DATE_TIME_KEYS.has(key)) return fmtDateTimeMsk(String(value));
  if (key === "day") return fmtDate(String(value));
  if (key === "amount" && typeof value === "number") return fmtRub(value);
  if (typeof value === "boolean") return value ? "да" : "нет";
  if (typeof value === "number" && NUMERIC_KEYS.has(key)) return ruNum.format(value);
  return String(value);
}

// --- отгрузки мимо портала ------------------------------------------------------

/** Сверка проведения с 1С словами: когда последняя удачная и что сломано. */
export function postingSyncText(sync: PostingSync): string {
  const ok = sync.last_ok_at ? fmtDateTimeMsk(sync.last_ok_at) : "";
  if (sync.partial) {
    const done = sync.last_done_at ? ` (${fmtDateTimeMsk(sync.last_done_at)})` : "";
    return (
      `Сверка проведения расходных с 1С прошла с ошибками${done}: не сверено документов — ` +
      `${ruNum.format(sync.failed)}. ${sync.last_error ?? ""}`.trimEnd()
    );
  }
  if (sync.last_error) {
    return (
      `Сверка проведения расходных с 1С упала: ${sync.last_error}` +
      (ok ? ` (последняя удачная — ${ok})` : " (удачных сверок ещё не было)")
    );
  }
  if (!sync.last_ok_at) return "Сверка проведения расходных с 1С ещё не выполнялась";
  if (sync.stale) return `Сверка проведения расходных с 1С устарела: последняя удачная — ${ok}`;
  return `Проведение расходных сверено с 1С: ${ok}`;
}

/** Период «Отгрузок мимо портала» при входе: три месяца по сегодня. */
export function defaultBypassPeriod(todayIso: string): [string, string] {
  const y = Number(todayIso.slice(0, 4));
  const m = Number(todayIso.slice(5, 7)) - 2;
  const from = new Date(Date.UTC(y, m - 1, 1));
  return [from.toISOString().slice(0, 10), todayIso];
}

const MONTHS = [
  "январь", "февраль", "март", "апрель", "май", "июнь",
  "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
];

/** `2026-09` → «сентябрь 2026». */
export function monthTitle(month: string): string {
  const idx = Number(month.slice(5, 7)) - 1;
  return `${MONTHS[idx] ?? month} ${month.slice(0, 4)}`;
}

/** Доля мимо портала: «—», когда отгрузок нет (не 0 %). */
export function bypassPctText(c: BypassCounts): string {
  return c.bypass_pct === null ? "—" : `${ruNum.format(c.bypass_pct)} %`;
}

/** Исключённое (другая база 1С) — строки для блока; пусто — показывать нечего. */
export function excludedLines(ex: Excluded): string[] {
  if (!ex.known) return [ex.title];
  const parts: string[] = [];
  if (ex.assembly_pallets) parts.push(`сборка — ${ruNum.format(ex.assembly_pallets)} пал.`);
  if (ex.shipment_pallets) parts.push(`отгрузка — ${ruNum.format(ex.shipment_pallets)} пал.`);
  if (ex.standing_pallets) parts.push(`собранные паллеты — ${ruNum.format(ex.standing_pallets)}`);
  if (parts.length === 0) return [];
  const out = [`${ex.title}. Не посчитано: ${parts.join(", ")}.`];
  if (ex.sessions.length) out.push(`Сессии: ${ex.sessions.join(", ")}.`);
  if (ex.other_bases.length) out.push(`База: ${ex.other_bases.join(", ")}.`);
  return out;
}

/** «Без сигнала» по состояниям документа — словами. */
export function noneStatesText(c: BypassCounts, titles: Record<string, string>): string {
  return Object.entries(c.none_states)
    .sort((a, b) => b[1] - a[1])
    .map(([state, n]) => `${titles[state] ?? state} — ${ruNum.format(n)}`)
    .join("; ");
}

export const BYPASS_PALLET_COLUMNS: DetailColumn[] = [
  { key: "pallet_number", title: "Паллета" },
  { key: "pallet_archive_number", title: "№ 1С (архив)" },
  { key: "order_number", title: "Заказ" },
  { key: "direction_title", title: "Направление" },
  { key: "organization_name", title: "Юрлицо" },
  { key: "kontragent_name", title: "Покупатель" },
  { key: "assembled_at", title: "Собрана" },
  { key: "signal_title", title: "Как ушла" },
  { key: "scanned_at", title: "Скан отгрузки" },
  { key: "posted_at", title: "Проведена в 1С (дата документа)" },
  { key: "expense_number", title: "Документ сборки" },
  { key: "posting_state_title", title: "Документ в 1С" },
];
